import { useEffect, useState } from 'react'

const STORAGE_KEY = 'a11yPrefs'

interface A11yPrefs {
  highContrast: boolean
  largeText: boolean
}

function loadPrefs(): A11yPrefs {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      // No explicit choice saved yet — default high contrast from the OS-level preference
      // (prefers-contrast: more) instead of always starting off. Once the user toggles it
      // themselves (either direction), that explicit choice is what gets saved and wins from
      // then on, regardless of what the OS preference says.
      const osPrefersMoreContrast = window.matchMedia?.('(prefers-contrast: more)').matches ?? false
      return { highContrast: osPrefersMoreContrast, largeText: false }
    }
    const parsed = JSON.parse(raw)
    return { highContrast: !!parsed.highContrast, largeText: !!parsed.largeText }
  } catch {
    return { highContrast: false, largeText: false }
  }
}

/** Persists high-contrast/large-text toggles and reflects them onto <html> as classes so
 *  index.css can style the whole app from one place, rather than threading a theme prop
 *  through every component's existing inline styles. */
export function useAccessibilityPrefs() {
  const [prefs, setPrefs] = useState<A11yPrefs>(loadPrefs)

  useEffect(() => {
    document.documentElement.classList.toggle('a11y-high-contrast', prefs.highContrast)
    document.documentElement.classList.toggle('a11y-large-text', prefs.largeText)
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
  }, [prefs])

  function toggleHighContrast() {
    setPrefs(p => ({ ...p, highContrast: !p.highContrast }))
  }

  function toggleLargeText() {
    setPrefs(p => ({ ...p, largeText: !p.largeText }))
  }

  return { ...prefs, toggleHighContrast, toggleLargeText }
}
